import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import UploadDownloadArea from "./UploadDownloadArea";
import DemoVideoPicker from "./DemoVideoPicker";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * Letter P — "Spreadsheet ↔ Timeline."
 *
 * Isaac's brief, fused: the REAL embedded ET on top, and directly under it a live,
 * Premiere-style timeline. Ticking clips in the real editor broadcasts the ordered pick list
 * over the embed pick bridge (createVideo.jsx posts `cc-et-picks` when framed), and the picks
 * land on the track below — the edit becomes visible the moment you make it. Below the fold:
 * the full "power story" — every dimension you can bend the spreadsheet along, the honest
 * combinatorics of it, the two ways to edit (Black Cherry vs your hand), and the
 * timeline/Premiere story (EDL/XML export is real TODAY; the panel plugin is the roadmap).
 *
 * Every claim is honest to ET_BEHAVIOR_SPEC.md. Nothing here is invented.
 */

const font = "Outfit, sans-serif";
const mono = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";
const CHERRY = "#e61d50";
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const iframeStyle = (scale: number): React.CSSProperties => ({
  position: "absolute", top: 0, left: 0, width: `${100 / scale}%`, height: `${100 / scale}%`,
  transform: `scale(${scale})`, transformOrigin: "top left", border: "none", background: "#0b0a0f",
});
const zoomBtn = { cursor: "pointer", background: "transparent", border: "none", color: "#c2c7d6", fontFamily: font, fontSize: "14px", lineHeight: 1, px: "8px", py: "3px", "&:hover": { color: "#fff", background: "rgba(255,255,255,0.08)" } };

/* ── the pick bridge payload (mirrors createVideo.jsx's cc-et-picks message) ── */
type ClipId = number | string; // split pieces get string ids ("123-a")
type Pick = {
  id: ClipId; order: number; start: number; end: number; sec: number; trimmed?: boolean;
  sentence: string; speaker: string; sentiment: string; thumb: string;
};
/** Commands the timeline can send back to the embedded ET (see createVideo.jsx bridge).
 *  Trim deltas are integer steps of 0.1s; a drag gesture batches into one command (one undo). */
type EtCmd =
  | { cmd: "adjust"; id: ClipId; deltaStart: number; deltaEnd: number }
  | { cmd: "reorder"; ids: ClipId[] }
  | { cmd: "deselect"; id: ClipId }
  | { cmd: "split"; id: ClipId; t: number }
  | { cmd: "ai-chat"; text: string };
type SendCmd = (c: EtCmd) => void;
type ChatMsg = { role: "user" | "assistant"; text: string; applied?: number };

/* ══════════════════ REAL FILMSTRIP FRAMES (client-side extraction) ══════════════════ */
/** The backend only thumbnails one poster per clip, so the timeline used to repeat that one
 *  frame. This extracts REAL frames in the browser: a hidden <video> seeks through the clip
 *  (~1 frame per second, ≤8 tiles) and grabs each frame onto a canvas. If the video host
 *  blocks canvas reads (CORS), we quietly fall back to the repeated poster. */
const frameCache = new Map<string, string>(); // "url|t" -> dataURL
const brokenFrameUrls = new Set<string>(); // hosts where canvas reads are CORS-tainted
type Extractor = { url: string; video: HTMLVideoElement; queue: number[]; busy: boolean; listeners: Set<() => void> };
let frameExtractor: Extractor | null = null;

const gridT = (t: number): number => Math.round(t * 2) / 2; // 0.5s grid → cache hits across trims

function pumpFrames() {
  const ex = frameExtractor;
  if (!ex || ex.busy) return;
  const t = ex.queue.shift();
  if (t == null) return;
  const key = `${ex.url}|${t}`;
  if (frameCache.has(key) || brokenFrameUrls.has(ex.url)) { pumpFrames(); return; }
  ex.busy = true;
  const done = () => { ex.busy = false; ex.listeners.forEach((l) => l()); pumpFrames(); };
  const onSeeked = () => {
    ex.video.removeEventListener("seeked", onSeeked);
    try {
      const c = document.createElement("canvas");
      c.width = 160; c.height = 90;
      const ctx = c.getContext("2d");
      if (ctx) {
        ctx.drawImage(ex.video, 0, 0, 160, 90);
        frameCache.set(key, c.toDataURL("image/jpeg", 0.6));
      }
    } catch (e) {
      brokenFrameUrls.add(ex.url); // canvas tainted → poster fallback everywhere
      ex.queue.length = 0;
    }
    done();
  };
  ex.video.addEventListener("seeked", onSeeked);
  try { ex.video.currentTime = t; } catch (e) { ex.video.removeEventListener("seeked", onSeeked); done(); }
}

function requestFrames(url: string, times: number[], listener: () => void): () => void {
  if (!url || brokenFrameUrls.has(url)) return () => undefined;
  if (!frameExtractor || frameExtractor.url !== url) {
    if (frameExtractor) { try { frameExtractor.video.src = ""; } catch (e) { /* ignore */ } }
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "auto";
    video.src = url;
    video.addEventListener("error", () => { if (frameExtractor && frameExtractor.url === url) brokenFrameUrls.add(url); });
    frameExtractor = { url, video, queue: [], busy: false, listeners: new Set() };
  }
  const ex = frameExtractor;
  ex.listeners.add(listener);
  times.forEach((t) => {
    const g = gridT(t);
    if (!frameCache.has(`${url}|${g}`) && !ex.queue.includes(g)) ex.queue.push(g);
  });
  if (ex.video.readyState >= 1) pumpFrames();
  else ex.video.addEventListener("loadedmetadata", () => pumpFrames(), { once: true });
  return () => { ex.listeners.delete(listener); };
}

/** One clip's strip of real frames: ~1 tile per second (≤8), poster-repeat fallback. */
const FrameTiles: React.FC<{ url: string; start: number; end: number; poster: string }> = ({ url, start, end, poster }) => {
  const [, force] = useState(0);
  const d = Math.max(0.1, end - start);
  const tiles = d > 1 ? Math.min(8, Math.floor(d)) : 1;
  const t0 = gridT(start);
  const t1 = gridT(end);
  const times = useMemo(
    () => Array.from({ length: tiles }, (_, k) => gridT(start + ((k + 0.5) * d) / tiles)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tiles, t0, t1]
  );
  useEffect(() => {
    if (!url || brokenFrameUrls.has(url)) return undefined;
    return requestFrames(url, times, () => force((x) => x + 1));
  }, [url, times]);

  if (!url || brokenFrameUrls.has(url)) {
    return (
      <Box sx={{ position: "absolute", inset: 0, backgroundImage: poster ? `url(${poster})` : "none", backgroundSize: "auto 100%", backgroundRepeat: "repeat-x", backgroundPosition: "left center" }} />
    );
  }
  return (
    <Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
      {times.map((t, i) => {
        const f = frameCache.get(`${url}|${t}`);
        return (
          <Box key={i} sx={{ flex: 1, minWidth: 0, backgroundImage: f ? `url(${f})` : poster ? `url(${poster})` : "none", backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#101017", borderRight: i < times.length - 1 ? "1px solid rgba(0,0,0,0.45)" : "none" }} />
        );
      })}
    </Box>
  );
};

const SPEAKER_HUES: Record<string, string> = {};
const speakerColor = (speaker: string): string => {
  if (!SPEAKER_HUES[speaker]) {
    const palette = ["#4f8df9", "#2fbf8f", "#c77dff", "#f2a33c", "#39c0d4", "#e6659a"];
    SPEAKER_HUES[speaker] = palette[Object.keys(SPEAKER_HUES).length % palette.length];
  }
  return SPEAKER_HUES[speaker];
};

// NOTE: the bridge sends start/end in SECONDS (verified live against the local share),
// despite the row model docs calling them ms. All timeline math here is in seconds.
const fmtTime = (sec: number): string => {
  const s = Math.max(0, Math.round(sec));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
const dur = (p: Pick): number => (p.sec && p.sec > 0 ? p.sec : Math.max(0, p.end - p.start));
const idNum = (id: ClipId): number => (typeof id === "number" ? id : id.split("").reduce((a, c) => a + c.charCodeAt(0), 0));

/* ══════════════════════════ THE CUT PLAYER ══════════════════════════ */
/** Plays THE CUT: two stacked <video> elements on the shared source, double-buffered — while
 *  one plays clip N, the hidden one is already parked on clip N+1's first frame, and a 60fps
 *  rAF engine (not the ~4Hz timeupdate event) swaps them at the boundary. That's what makes
 *  non-chronological cuts play as if they were one file. Appears only once clips are picked;
 *  docks at the right end of the timeline, pops out to a draggable window. */
const fmtSec1 = (s: number): string => {
  const m = Math.floor(Math.max(0, s) / 60);
  const r = Math.max(0, s) - m * 60;
  return `${String(m).padStart(2, "0")}:${r < 10 ? "0" : ""}${r.toFixed(1)}`;
};
const offsFor = (P: Pick[], i: number): number => P.slice(0, i).reduce((a, p) => a + dur(p), 0);

type PlayerProgress = { playingId: ClipId | null; clipT: number; globalT: number; playing: boolean };

const CutPlayer: React.FC<{
  picks: Pick[];
  videoUrl: string;
  seekReq: { id: ClipId; nonce: number } | null;
  onProgress: (p: PlayerProgress) => void;
  floating: boolean;
  onToggleFloat: () => void;
  music: { url: string; vol: number };
}> = ({ picks, videoUrl, seekReq, onProgress, floating, onToggleFloat, music }) => {
  const vARef = useRef<HTMLVideoElement | null>(null);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const vBRef = useRef<HTMLVideoElement | null>(null);
  const [face, setFace] = useState<0 | 1>(0); // which buffer is on screen
  const [currentId, setCurrentId] = useState<ClipId | null>(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false); // autoplay refused → ask for a tap
  const [clock, setClock] = useState(0); // cut-relative seconds, throttled
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastReportRef = useRef(0);
  // live mirror so the 60fps loop never reads a stale closure
  const liveRef = useRef<{ picks: Pick[]; idx: number; playing: boolean; face: 0 | 1 }>({ picks: [], idx: 0, playing: false, face: 0 });

  const idx = Math.max(0, picks.findIndex((p) => p.id === currentId));
  const clip = picks.length ? picks[Math.min(idx, picks.length - 1)] : null;
  const total = picks.reduce((a, p) => a + dur(p), 0);
  liveRef.current = { picks, idx, playing, face };

  const elOf = (f: 0 | 1) => (f === 0 ? vARef.current : vBRef.current);
  const seekEl = (v: HTMLVideoElement | null, t: number) => { if (v) { try { v.currentTime = t; } catch (e) { /* ignore */ } } };

  const report = (globalT: number, playingId: ClipId | null, isPlaying: boolean, clipT: number, force = false) => {
    const now = performance.now();
    if (!force && now - lastReportRef.current < 100) return;
    lastReportRef.current = now;
    setClock(globalT);
    onProgress({ playingId, clipT, globalT, playing: isPlaying });
  };

  // keep the standby buffer parked on the NEXT clip's first frame
  useEffect(() => {
    const nx = picks[idx + 1];
    const sb = elOf(face === 0 ? 1 : 0);
    if (sb && nx && Math.abs(sb.currentTime - nx.start) > 0.05) { sb.pause(); seekEl(sb, nx.start); }
  }, [picks, idx, face]);

  // keep a sane current clip when picks change (removals, reorders)
  useEffect(() => {
    if (!picks.length) { setCurrentId(null); setPlaying(false); liveRef.current.playing = false; return; }
    if (currentId == null || !picks.some((p) => p.id === currentId)) {
      setCurrentId(picks[0].id);
      seekEl(elOf(face), picks[0].start);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks]);

  // clicking a clip in the timeline seeks the player there
  useEffect(() => {
    if (!seekReq) return;
    const p = picks.find((x) => x.id === seekReq.id);
    if (!p) return;
    setCurrentId(p.id);
    seekEl(elOf(liveRef.current.face), p.start);
    report(offsFor(picks, picks.findIndex((x) => x.id === p.id)), p.id, liveRef.current.playing, 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seekReq]);

  // ── the boundary engine: 60fps, double-buffered swap at each clip's OUT ──
  const loop = () => {
    const { picks: P, idx: i, playing: pl, face: f } = liveRef.current;
    if (!pl) { rafRef.current = null; return; }
    const v = elOf(f);
    const c = P[i];
    if (v && c) {
      if (v.currentTime >= c.end - 0.02 || v.ended) {
        const nx = P[i + 1];
        if (nx) {
          const sb = elOf(f === 0 ? 1 : 0);
          v.pause();
          if (sb) {
            if (Math.abs(sb.currentTime - nx.start) > 0.25) seekEl(sb, nx.start);
            sb.play().catch(() => { /* ignore */ });
          }
          const nf = f === 0 ? 1 : 0;
          liveRef.current = { ...liveRef.current, face: nf, idx: i + 1 };
          setFace(nf);
          setCurrentId(nx.id);
          report(offsFor(P, i + 1), nx.id, true, 0, true);
        } else {
          v.pause();
          const bg = musicRef.current;
          if (bg) bg.pause();
          liveRef.current.playing = false;
          setPlaying(false);
          report(total, c.id, false, dur(c), true);
          rafRef.current = null;
          return;
        }
      } else {
        const clipT = clamp(v.currentTime - c.start, 0, dur(c));
        report(offsFor(P, i) + clipT, c.id, true, clipT);
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  };

  const play = () => {
    const v = elOf(liveRef.current.face);
    if (!v || !clip) return;
    if (v.currentTime < clip.start - 0.2 || v.currentTime > clip.end - 0.05) seekEl(v, clip.start);
    v.play().then(() => {
      setBlocked(false);
      setPlaying(true);
      liveRef.current.playing = true;
      if (music.url && musicRef.current) { musicRef.current.volume = clamp(music.vol, 0, 1); musicRef.current.play().catch(() => { /* ignore */ }); }
      if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
    }).catch(() => setBlocked(true));
  };
  const pause = () => {
    const v = elOf(liveRef.current.face);
    if (v) v.pause();
    if (musicRef.current) musicRef.current.pause();
    setPlaying(false);
    liveRef.current.playing = false;
  };
  const step = (d: 1 | -1) => {
    const ni = clamp(idx + d, 0, picks.length - 1);
    const p = picks[ni];
    if (!p) return;
    setCurrentId(p.id);
    seekEl(elOf(liveRef.current.face), p.start);
    report(offsFor(picks, ni), p.id, liveRef.current.playing, 0, true);
  };
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // M1 music bed: loops under the cut in PREVIEW (exports don't mix music yet).
  useEffect(() => { if (musicRef.current) musicRef.current.volume = clamp(music.vol, 0, 1); }, [music.vol]);
  useEffect(() => {
    const a = musicRef.current;
    if (!a) return;
    if (!music.url) { a.pause(); return; }
    if (liveRef.current.playing) { a.play().catch(() => { /* ignore */ }); }
  }, [music.url]);

  // draggable when floating
  const onDragStart = (e: React.PointerEvent) => {
    if (!floating) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    const move = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setPos({ x: d.ox + (ev.clientX - d.sx), y: d.oy + (ev.clientY - d.sy) });
    };
    const up = () => { dragRef.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  if (!picks.length || !videoUrl) return null;

  const card = (
    <Box sx={{ display: "flex", flexDirection: "column", borderRadius: "10px", overflow: "hidden", border: `1px solid ${floating ? "rgba(230,29,80,0.55)" : "rgba(255,255,255,0.14)"}`, background: "#08070b", boxShadow: floating ? "0 24px 70px -20px rgba(0,0,0,0.95)" : "none", width: floating ? { xs: "300px", md: "360px" } : "100%", pointerEvents: "auto" }}>
      {/* header — drag handle when floating */}
      <Box onPointerDown={onDragStart}
        sx={{ display: "flex", alignItems: "center", gap: "8px", px: "10px", py: "6px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)", cursor: floating ? "grab" : "default", userSelect: "none" }}>
        <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", background: CHERRY }} />
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "11.5px", color: "#f3f4f9" }}>Your cut — preview</Typography>
        <Box sx={{ flex: 1 }} />
        {floating && <Typography sx={{ fontFamily: font, fontSize: "10px", color: "#8d94a8" }}>drag me</Typography>}
        <Box component="button" onClick={onToggleFloat} title={floating ? "Dock back into the timeline" : "Pop out — move it anywhere"}
          sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "6px", color: "#c2c7d6", fontFamily: font, fontSize: "10.5px", px: "7px", py: "3px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.35)" } }}>
          {floating ? "⇲ dock" : "⇱ pop out"}
        </Box>
      </Box>
      {/* the double-buffered video pair */}
      <Box sx={{ position: "relative", background: "#000", aspectRatio: "16 / 9" }}>
        {music.url ? <audio ref={musicRef} src={music.url} loop preload="auto" /> : null}
        {([0, 1] as const).map((f) => (
          <video
            key={f}
            ref={f === 0 ? vARef : vBRef}
            src={videoUrl}
            playsInline
            preload="auto"
            onClick={() => (playing ? pause() : play())}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", cursor: "pointer", opacity: face === f ? 1 : 0 }}
          />
        ))}
        {!playing && (
          <Box onClick={play} sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "rgba(0,0,0,0.25)" }}>
            <Box sx={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(230,29,80,0.92)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 26px -8px rgba(230,29,80,0.8)" }}>
              <Typography sx={{ color: "#fff", fontSize: "18px", ml: "3px" }}>▶</Typography>
            </Box>
          </Box>
        )}
        {blocked && (
          <Typography sx={{ position: "absolute", bottom: 6, left: 0, right: 0, textAlign: "center", fontFamily: font, fontSize: "10.5px", color: "#f4c9d6" }}>
            tap ▶ to start (browser blocked autoplay)
          </Typography>
        )}
      </Box>
      {/* transport */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "8px", py: "6px" }}>
        {[
          { t: "⏮", fn: () => step(-1), title: "Previous clip" },
          { t: playing ? "⏸" : "▶", fn: () => (playing ? pause() : play()), title: playing ? "Pause" : "Play the cut" },
          { t: "⏭", fn: () => step(1), title: "Next clip" },
        ].map((b) => (
          <Box key={b.title} component="button" onClick={b.fn} title={b.title}
            sx={{ cursor: "pointer", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "6px", color: "#fff", fontFamily: font, fontSize: "12px", px: "9px", py: "4px", lineHeight: 1, "&:hover": { background: "rgba(230,29,80,0.25)" } }}>
            {b.t}
          </Box>
        ))}
        <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#8d94a8", ml: "2px", whiteSpace: "nowrap" }}>
          clip {Math.min(idx + 1, picks.length)}/{picks.length}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#8d94a8", whiteSpace: "nowrap" }}>
          {fmtSec1(clock)} / {fmtSec1(total)}
        </Typography>
      </Box>
    </Box>
  );

  if (floating) {
    return (
      <Box sx={{ position: "fixed", zIndex: 12500, left: { xs: "12px", md: "auto" }, right: { md: "36px" }, bottom: "88px", transform: `translate(${pos.x}px, ${pos.y}px)`, pointerEvents: "none" }}>
        {card}
      </Box>
    );
  }
  return card;
};

/* ══════════════════════════ THE CHAT DOCK ══════════════════════════ */
/** A real chat input — the familiar ChatGPT/Claude pill — sitting right under the editor,
 *  where the Select-clips / Export pill lives. It IS Black Cherry: messages go over the
 *  bridge into the ET's own chat pipeline (guest AI on shares, your key when logged in),
 *  the AI's picks tick themselves in the sheet and land on the timeline, and the reply
 *  comes back here. One brain, two doorways. */
const ChatDock: React.FC<{ thread: ChatMsg[]; busy: boolean; onSend: (text: string) => void }> = ({ thread, busy, onSend }) => {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(true); // the conversation panel can be collapsed to just the pill
  const lastLenRef = useRef(0);
  const threadRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, busy, open]);
  // a NEW message re-opens the panel (a manual close stays closed until then)
  useEffect(() => {
    if (thread.length > lastLenRef.current) setOpen(true);
    lastLenRef.current = thread.length;
  }, [thread.length]);
  const send = () => {
    const t = text.trim();
    if (!t || busy) return;
    onSend(t);
    setText("");
  };
  return (
    <Box sx={{ width: "100%", maxWidth: "640px", mx: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
      {/* the conversation — appears once there's something to show; collapsible */}
      {(thread.length > 0 || busy) && open && (
        <Box sx={{ position: "relative" }}>
          <Box component="button" onClick={() => setOpen(false)} title="Hide the conversation (the chat pill stays)"
            sx={{ position: "absolute", top: "-9px", right: "8px", zIndex: 1, cursor: "pointer", px: "8px", py: "2px", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.18)", background: "#0d0c12", color: "#8d94a8", fontFamily: font, fontSize: "10px", fontWeight: 700, lineHeight: 1.4, "&:hover": { color: "#fff", borderColor: "rgba(230,29,80,0.6)" } }}>
            ✕ hide
          </Box>
        <Box ref={threadRef} sx={{ maxHeight: "150px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px", p: "8px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.09)", background: "rgba(8,7,11,0.6)" }}>
          {thread.map((m, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <Box sx={{ maxWidth: "86%", px: "10px", py: "6px", borderRadius: m.role === "user" ? "12px 12px 3px 12px" : "12px 12px 12px 3px", background: m.role === "user" ? "rgba(230,29,80,0.18)" : "rgba(255,255,255,0.05)", border: `1px solid ${m.role === "user" ? "rgba(230,29,80,0.4)" : "rgba(255,255,255,0.1)"}` }}>
                <Typography sx={{ fontFamily: font, fontSize: "12px", lineHeight: 1.5, color: "#e6e8f0", whiteSpace: "pre-wrap" }}>
                  {m.role === "assistant" && <Box component="span" aria-hidden sx={{ mr: "5px" }}>🍒</Box>}
                  {m.text}
                </Typography>
                {m.role === "assistant" && (m.applied || 0) > 0 && (
                  <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#f4c9d6", mt: "3px" }}>
                    ✓ {m.applied} clip{m.applied === 1 ? "" : "s"} ticked — they're on the timeline below
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
          {busy && (
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: "#9aa0b4", px: "4px" }}>
              🍒 Black Cherry is cutting<Box component="span" sx={{ "@keyframes ccDots": { "0%": { opacity: 0.2 }, "50%": { opacity: 1 }, "100%": { opacity: 0.2 } }, animation: "ccDots 1.2s infinite" }}>…</Box>
            </Typography>
          )}
        </Box>
        </Box>
      )}
      {thread.length > 0 && !open && (
        <Box component="button" onClick={() => setOpen(true)}
          sx={{ alignSelf: "center", cursor: "pointer", px: "10px", py: "3px", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: "#8d94a8", fontFamily: font, fontSize: "10.5px", fontWeight: 700, "&:hover": { color: "#fff", borderColor: "rgba(230,29,80,0.6)" } }}>
          show conversation ({thread.length})
        </Box>
      )}
      {/* the pill */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "8px", py: "6px", borderRadius: "999px", border: "1px solid rgba(255,255,255,0.16)", background: "#0d0c12", boxShadow: "0 10px 30px -18px rgba(0,0,0,0.9)", "&:focus-within": { borderColor: "rgba(230,29,80,0.6)" } }}>
        <Box aria-hidden sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: "linear-gradient(135deg,#5a2bd0,#e61d50)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "12px", lineHeight: 1 }}>🍒</Typography>
        </Box>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder='Ask Black Cherry — “pick the 3 strongest moments and put the hook first”'
          style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "#e6e8f0", fontFamily: "Outfit, sans-serif", fontSize: "13px", padding: "4px 2px" }}
        />
        <Box component="button" onClick={send} aria-label="Send" title={busy ? "Black Cherry is thinking…" : "Send"}
          sx={{ flexShrink: 0, cursor: busy || !text.trim() ? "default" : "pointer", width: 30, height: 30, borderRadius: "50%", border: "none", background: busy || !text.trim() ? "rgba(255,255,255,0.08)" : CHERRY, color: "#fff", fontSize: "14px", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s", "&:hover": { background: busy || !text.trim() ? "rgba(255,255,255,0.08)" : "#c81834" } }}>
          ➤
        </Box>
      </Box>
    </Box>
  );
};

/* ══════════════════════════ THE TIMELINE ══════════════════════════ */
/** The editing surface under the spreadsheet. Two honest views of the same cut:
 *  ▦ Storyboard — every pick as an equal card, in pick order (calm, scannable);
 *  ⣿ Timeline  — Premiere/CapCut-style: width = true duration, filmstrip posters,
 *                and GRAB-THE-ENDS trimming: drag a selected clip's edge, and on release the
 *                whole gesture lands as one batched ±0.1s trim in the sheet (one undo).
 *  The clip tools live in the header row (next to the view toggle) so the tracks get the space.
 *  Every edit round-trips through the ET — the sheet and the timeline never disagree. */
const EditTimeline: React.FC<{
  picks: Pick[];
  videoUrl: string;
  sendCmd: SendCmd;
}> = ({ picks, videoUrl, sendCmd }) => {
  const totalSec = picks.reduce((a, p) => a + dur(p), 0);
  const [mode, setMode] = useState<"storyboard" | "timeline">("storyboard");
  const [selectedId, setSelectedId] = useState<ClipId | null>(null);
  const [seekReq, setSeekReq] = useState<{ id: ClipId; nonce: number } | null>(null);
  const [floating, setFloating] = useState(false);
  const [prog, setProg] = useState<PlayerProgress>({ playingId: null, clipT: 0, globalT: 0, playing: false });
  // M1 music bed (preview-only): tracks come from /music/manifest.json — drop your own
  // instrumentals (e.g. downloaded from your Suno library) into emofront/public/music/.
  const [musicList, setMusicList] = useState<{ file: string; title: string; description?: string; vocal?: boolean }[]>([]);
  const [musicUrl, setMusicUrl] = useState("");
  const [musicVol, setMusicVol] = useState(0.25);
  useEffect(() => {
    fetch("/music/manifest.json")
      .then(async (r) => (r.ok ? r.json() : []))
      .then((j) => { if (Array.isArray(j)) setMusicList(j.filter((t) => t && t.file)); })
      .catch(() => { /* no library — the picker still offers "your own file" */ });
  }, []);
  // drag-trim: optimistic preview in 0.1s steps until the ET echoes the authoritative times
  const [preview, setPreview] = useState<{ id: ClipId; dIn: number; dOut: number } | null>(null);
  const seekNonce = useRef(0);
  const trackRef = useRef<HTMLDivElement | null>(null);

  // a removed clip can't stay selected; fresh picks supersede any drag preview
  useEffect(() => {
    if (selectedId != null && !picks.some((p) => p.id === selectedId)) setSelectedId(null);
    setPreview(null);
  }, [picks, selectedId]);

  const eff = (p: Pick): { start: number; end: number } =>
    preview && preview.id === p.id
      ? { start: p.start + preview.dIn * 0.1, end: p.end + preview.dOut * 0.1 }
      : { start: p.start, end: p.end };
  const effDur = (p: Pick): number => { const e = eff(p); return Math.max(0.1, e.end - e.start); };

  // Ruler ticks across the assembled edit (not the source video — this IS your cut).
  const ticks = useMemo(() => {
    if (!totalSec) return [];
    const step = totalSec > 120 ? 30 : totalSec > 30 ? 10 : totalSec > 8 ? 5 : 1;
    const out: { at: number; label: string }[] = [];
    for (let t = 0; t <= totalSec; t += step) out.push({ at: (t / totalSec) * 100, label: fmtTime(t) });
    return out;
  }, [totalSec]);

  const sel = selectedId != null ? picks.find((p) => p.id === selectedId) || null : null;
  const selectClip = (p: Pick) => {
    if (selectedId === p.id) { setSelectedId(null); return; } // click again = done editing
    setSelectedId(p.id);
    seekNonce.current += 1;
    setSeekReq({ id: p.id, nonce: seekNonce.current });
  };
  // Esc = done editing (nothing is lost — trims are already applied & undoable in the sheet)
  useEffect(() => {
    if (selectedId == null) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSelectedId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId]);
  const move = (dir: 1 | -1) => {
    if (!sel) return;
    const i = picks.findIndex((p) => p.id === sel.id);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= picks.length) return;
    const ids = picks.map((p) => p.id);
    const tmp = ids[i]; ids[i] = ids[j]; ids[j] = tmp;
    sendCmd({ cmd: "reorder", ids });
  };
  const removeSel = () => { if (sel) { sendCmd({ cmd: "deselect", id: sel.id }); setSelectedId(null); } };
  // ✂ split at the playhead if it's inside this clip, else at the clip's middle. The ET
  // snaps to the nearest word boundary (same rule as right-click-a-word in the sheet).
  const splitSel = () => {
    if (!sel) return;
    const t = prog.playingId === sel.id ? sel.start + prog.clipT : (sel.start + sel.end) / 2;
    sendCmd({ cmd: "split", id: sel.id, t });
    setSelectedId(null);
  };
  const adjust = (which: "in" | "out", steps: number) => {
    if (!sel || !steps) return;
    sendCmd({
      cmd: "adjust",
      id: sel.id,
      deltaStart: which === "in" ? steps : 0,
      deltaEnd: which === "out" ? steps : 0,
    });
  };

  // ── grab-the-ends trimming (Timeline view): drag an edge, release = one batched trim ──
  const onHandleDown = (e: React.PointerEvent, p: Pick, side: "in" | "out") => {
    e.stopPropagation();
    e.preventDefault();
    if (selectedId !== p.id) selectClip(p);
    const track = trackRef.current;
    if (!track || !totalSec) return;
    const pxPerSec = track.getBoundingClientRect().width / totalSec;
    if (pxPerSec <= 0) return;
    const startX = e.clientX;
    const maxShrink = Math.floor((dur(p) - 0.2) / 0.1); // keep ≥0.2s of clip
    let steps = 0;
    const onMove = (ev: PointerEvent) => {
      const dSec = (ev.clientX - startX) / pxPerSec;
      let s = Math.round(dSec / 0.1);
      if (side === "in") s = clamp(s, -600, Math.max(0, maxShrink));       // IN → right shrinks
      else s = clamp(s, -Math.max(0, maxShrink), 600);                      // OUT → left shrinks
      if (s !== steps) {
        steps = s;
        setPreview({ id: p.id, dIn: side === "in" ? steps : 0, dOut: side === "out" ? steps : 0 });
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (steps !== 0) {
        sendCmd({
          cmd: "adjust",
          id: p.id,
          deltaStart: side === "in" ? steps : 0,
          deltaEnd: side === "out" ? steps : 0,
        });
      } else {
        setPreview(null);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const playheadPct = totalSec ? clamp((prog.globalT / totalSec) * 100, 0, 100) : 0;

  const modeBtn = (m: "storyboard" | "timeline", label: string, hint: string) => {
    const on = mode === m;
    return (
      <Box component="button" key={m} onClick={() => setMode(m)} title={hint}
        sx={{ cursor: "pointer", fontFamily: font, fontWeight: 700, fontSize: "11px", px: "10px", py: "4px", border: "none", background: on ? "linear-gradient(100deg,#e61d50,#b3123c)" : "transparent", color: on ? "#fff" : "#9aa0b4", "&:hover": { color: "#fff" } }}>
        {label}
      </Box>
    );
  };

  const trimBtn = (label: string, title: string, fn: () => void) => (
    <Box component="button" key={title} onClick={fn} title={title}
      sx={{ cursor: "pointer", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "6px", color: "#e6e8f0", fontFamily: mono, fontSize: "11px", fontWeight: 700, px: "8px", py: "4px", lineHeight: 1, "&:hover": { borderColor: CHERRY, color: "#fff" } }}>
      {label}
    </Box>
  );

  return (
    <Box sx={{ borderRadius: "0 0 14px 14px", border: "1px solid rgba(255,255,255,0.12)", borderTop: "none", background: "#0d0c12", overflow: "hidden" }}>
      {/* header bar — title · counts · view toggle · THE CLIP TOOLS (so the tracks keep the space) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "13px", py: "6px", borderBottom: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)", flexWrap: "wrap", minHeight: "38px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9" }}>Timeline — your cut</Typography>
        <Typography sx={{ fontFamily: mono, fontSize: "11.5px", color: "#8d94a8" }}>
          {picks.length ? `${picks.length} clip${picks.length === 1 ? "" : "s"} · ${fmtTime(totalSec)}` : "empty"}
        </Typography>
        <Box sx={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "999px", overflow: "hidden" }}>
          {modeBtn("storyboard", "▦ Storyboard", "Equal cards, in pick order — plays in this order")}
          {modeBtn("timeline", "⣿ Timeline", "True durations — grab a selected clip's ends to trim")}
        </Box>
        <Box sx={{ flex: 1 }} />
        {sel ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap" }}>
            <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: "#f4c9d6", whiteSpace: "nowrap" }}>
              #{sel.order} · {sel.speaker || "?"}
            </Typography>
            <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: "#8d94a8", whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>
              {fmtSec1(eff(sel).start)} → {fmtSec1(eff(sel).end)} ({effDur(sel).toFixed(1)}s)
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>IN</Typography>
            {trimBtn("−", "Start 0.1s earlier (steals from the previous clip)", () => adjust("in", -1))}
            {trimBtn("+", "Start 0.1s later", () => adjust("in", 1))}
            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>OUT</Typography>
            {trimBtn("−", "End 0.1s earlier", () => adjust("out", -1))}
            {trimBtn("+", "End 0.1s later (the next clip gives way)", () => adjust("out", 1))}
            {trimBtn("◀", "Move earlier in the cut", () => move(-1))}
            {trimBtn("▶", "Move later in the cut", () => move(1))}
            {trimBtn("✂", "Split at the playhead (nearest word) — both halves stay picked", splitSel)}
            {trimBtn("✕", "Remove from the cut (unticks it in the sheet)", removeSel)}
            <Box component="button" onClick={() => setSelectedId(null)} title="Done editing (Esc, or click the clip again). Your trims are saved — nothing to lose."
              sx={{ cursor: "pointer", background: "rgba(40,200,120,0.12)", border: "1px solid rgba(40,200,120,0.5)", borderRadius: "6px", color: "#6de0a8", fontFamily: font, fontSize: "11px", fontWeight: 700, px: "9px", py: "4px", lineHeight: 1, "&:hover": { background: "rgba(40,200,120,0.22)" } }}>
              ✓ Done
            </Box>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#8d94a8", display: { xs: "none", md: "block" } }}>
            {picks.length ? "Click a clip to trim, reorder, or remove — every edit updates the sheet." : "Exports in this exact order — MP4, or EDL / Premiere XML"}
          </Typography>
        )}
      </Box>

      {picks.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px", py: "34px", px: "16px" }}>
          <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "15px", color: "#c2c7d6", textAlign: "center" }}>
            <Box component="span" sx={{ color: CHERRY }}>↑</Box> Tick clips in the editor above — they land here, and the player appears.
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: "#8d94a8", textAlign: "center" }}>
            Cherry-pick by hand, or hit the blue <Box component="span" sx={{ color: "#9dbcff", fontWeight: 700 }}>Ask AI</Box> pill and let Black Cherry make the first pass.
          </Typography>
          {/* ghost track so the surface still reads as a timeline */}
          <Box sx={{ width: "100%", maxWidth: "860px", mt: "10px", display: "flex", gap: "4px", opacity: 0.22 }}>
            {[18, 9, 24, 13, 20, 16].map((w, i) => (
              <Box key={i} sx={{ flex: `0 0 ${w}%`, height: 44, borderRadius: "6px", border: "1px dashed rgba(255,255,255,0.35)" }} />
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "12px", px: "13px", py: "12px" }}>
          {/* ── LEFT: the tracks ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {mode === "timeline" && (
              <Box sx={{ position: "relative", height: "18px", ml: "46px", mb: "4px" }}>
                {ticks.map((t, i) => (
                  <Box key={i} sx={{ position: "absolute", left: `${t.at}%`, top: 0, transform: "translateX(-50%)" }}>
                    <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: "#6e7488", whiteSpace: "nowrap" }}>{t.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {mode === "storyboard" ? (
              /* ▦ STORYBOARD — every pick an equal card, in pick order */
              <Box sx={{ display: "flex", gap: "8px", overflowX: "auto", pb: "6px" }}>
                {picks.map((p) => {
                  const isSel = selectedId === p.id;
                  const isPlaying = prog.playingId === p.id;
                  return (
                    <Box key={p.id} onClick={() => selectClip(p)} title={`#${p.order} · ${p.speaker || "?"} · ${p.sentence}`}
                      sx={{ flex: "0 0 150px", cursor: "pointer", borderRadius: "8px", overflow: "hidden", border: `2px solid ${isSel ? CHERRY : isPlaying ? "#ff456a88" : "rgba(255,255,255,0.1)"}`, background: "#101017", transition: "border-color .12s, transform .12s", "&:hover": { transform: "translateY(-2px)" } }}>
                      <Box sx={{ position: "relative", height: "84px", background: p.thumb ? `url(${p.thumb}) center/cover` : `linear-gradient(180deg, ${speakerColor(p.speaker)}33, ${speakerColor(p.speaker)}14)` }}>
                        <Box sx={{ position: "absolute", top: 4, left: 4, minWidth: 17, height: 17, px: "3px", borderRadius: "4px", background: CHERRY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontFamily: mono, fontSize: "10px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{p.order}</Typography>
                        </Box>
                        {p.trimmed && (
                          <Box title="Trimmed — synced with the sheet" sx={{ position: "absolute", top: 4, right: 4, px: "4px", py: "1px", borderRadius: "4px", background: "rgba(20,184,166,0.85)" }}>
                            <Typography sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>✂</Typography>
                          </Box>
                        )}
                        <Box sx={{ position: "absolute", right: 4, bottom: 4, px: "4px", py: "1px", borderRadius: "4px", background: "rgba(8,7,11,0.75)" }}>
                          <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: "#e6e8f0", lineHeight: 1.3 }}>{dur(p).toFixed(1)}s</Typography>
                        </Box>
                        {/* live progress under the playing card */}
                        {isPlaying && (
                          <Box sx={{ position: "absolute", left: 0, bottom: 0, height: "3px", width: `${clamp((prog.clipT / Math.max(0.1, dur(p))) * 100, 0, 100)}%`, background: "#ff456a" }} />
                        )}
                      </Box>
                      <Box sx={{ px: "7px", py: "5px", borderTop: `2px solid ${speakerColor(p.speaker)}55` }}>
                        <Typography noWrap sx={{ fontFamily: font, fontSize: "10px", color: "#c2c7d6" }}>{p.sentence}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              /* ⣿ TIMELINE — true durations, filmstrip posters, grab-the-ends trimming */
              <>
                <Box sx={{ display: "flex", alignItems: "stretch", gap: "8px" }}>
                  <Box sx={{ flex: "0 0 38px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 700, color: "#8d94a8" }}>V1</Typography>
                  </Box>
                  <Box ref={trackRef} sx={{ position: "relative", flex: 1, minWidth: 0, display: "flex", gap: "3px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "8px", p: "4px", overflow: "hidden" }}>
                    {picks.map((p) => {
                      const w = totalSec ? (effDur(p) / totalSec) * 100 : 0;
                      const isSel = selectedId === p.id;
                      const dragging = preview && preview.id === p.id;
                      return (
                        <Box key={p.id} onClick={() => selectClip(p)} title={`#${p.order} · ${p.speaker || "?"} · ${p.sentence}`}
                          sx={{
                            position: "relative", flex: `0 0 calc(${w}% - 3px)`, minWidth: "30px", height: 64,
                            borderRadius: "6px", overflow: "hidden", cursor: "pointer",
                            border: `2px solid ${dragging ? "#ff456a" : isSel ? CHERRY : `${speakerColor(p.speaker)}66`}`,
                            background: `linear-gradient(180deg, ${speakerColor(p.speaker)}33, ${speakerColor(p.speaker)}14)`,
                            transition: dragging ? "none" : "border-color .12s",
                            "&:hover": { borderColor: isSel ? CHERRY : "#ff456a88" },
                          }}
                        >
                          {/* REAL frames (~1/sec, extracted client-side; poster-repeat fallback) */}
                          <FrameTiles url={videoUrl} start={eff(p).start} end={eff(p).end} poster={p.thumb} />
                          <Box aria-hidden sx={{ position: "absolute", inset: 0, background: "linear-gradient(rgba(10,9,14,0.15), rgba(10,9,14,0.45))" }} />
                          <Box sx={{ position: "absolute", top: 3, left: 3, minWidth: 16, height: 16, px: "3px", borderRadius: "4px", background: CHERRY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography sx={{ fontFamily: mono, fontSize: "9.5px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{p.order}</Typography>
                          </Box>
                          {p.trimmed && !isSel && (
                            <Box title="Trimmed — synced with the sheet" sx={{ position: "absolute", top: 3, right: 3, px: "4px", py: "1px", borderRadius: "4px", background: "rgba(20,184,166,0.85)" }}>
                              <Typography sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>✂</Typography>
                            </Box>
                          )}
                          <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, px: "5px", py: "2px", background: "linear-gradient(transparent, rgba(8,7,11,0.85))" }}>
                            <Typography noWrap sx={{ fontFamily: font, fontSize: "9.5px", color: "#e6e8f0" }}>{p.sentence}</Typography>
                          </Box>
                          {/* grab-the-ends handles — CapCut style, on the selected clip */}
                          {isSel && (
                            <>
                              <Box onPointerDown={(e: React.PointerEvent) => onHandleDown(e, p, "in")} title="Drag to trim the start"
                                sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "11px", cursor: "ew-resize", background: "linear-gradient(90deg, rgba(230,29,80,0.95), rgba(230,29,80,0.25))", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none" }}>
                                <Box sx={{ width: "2px", height: "22px", borderRadius: "2px", background: "#fff", opacity: 0.9 }} />
                              </Box>
                              <Box onPointerDown={(e: React.PointerEvent) => onHandleDown(e, p, "out")} title="Drag to trim the end"
                                sx={{ position: "absolute", top: 0, bottom: 0, right: 0, width: "11px", cursor: "ew-resize", background: "linear-gradient(270deg, rgba(230,29,80,0.95), rgba(230,29,80,0.25))", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none" }}>
                                <Box sx={{ width: "2px", height: "22px", borderRadius: "2px", background: "#fff", opacity: 0.9 }} />
                              </Box>
                            </>
                          )}
                          {/* live trim readout while dragging */}
                          {dragging && (
                            <Box sx={{ position: "absolute", top: 3, right: 3, px: "5px", py: "2px", borderRadius: "4px", background: "rgba(8,7,11,0.9)", border: "1px solid rgba(255,69,106,0.6)" }}>
                              <Typography sx={{ fontFamily: mono, fontSize: "9.5px", fontWeight: 700, color: "#ff9db1", lineHeight: 1.2 }}>
                                {effDur(p).toFixed(1)}s
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                    {/* playhead — driven by the real player, not an animation */}
                    {(prog.playing || prog.globalT > 0) && (
                      <Box aria-hidden sx={{ position: "absolute", top: 0, bottom: 0, left: `${playheadPct}%`, width: "2px", background: "#ff456a", boxShadow: "0 0 8px rgba(255,69,106,0.9)", pointerEvents: "none" }} />
                    )}
                  </Box>
                </Box>

                {/* A1 — audio strip (decorative bars under each clip, same widths) */}
                <Box sx={{ display: "flex", alignItems: "stretch", gap: "8px", mt: "5px" }}>
                  <Box sx={{ flex: "0 0 38px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 700, color: "#8d94a8" }}>A1</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: "3px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "8px", p: "4px" }}>
                    {picks.map((p) => {
                      const w = totalSec ? (effDur(p) / totalSec) * 100 : 0;
                      return (
                        <Box key={p.id} sx={{ flex: `0 0 calc(${w}% - 3px)`, minWidth: "30px", height: 20, borderRadius: "4px", overflow: "hidden", display: "flex", alignItems: "center", gap: "1.5px", px: "3px", background: `${speakerColor(p.speaker)}18`, border: `1px solid ${speakerColor(p.speaker)}40` }}>
                          {Array.from({ length: 24 }).map((_, i) => (
                            <Box key={i} sx={{ flex: 1, height: `${25 + ((idNum(p.id) * 7 + i * 13) % 60)}%`, borderRadius: "1px", background: `${speakerColor(p.speaker)}99` }} />
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            )}

            {/* ── M1: the music bed — your library first (Suno downloads welcome), preview-only ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "6px", flexWrap: "wrap" }}>
              <Box sx={{ flex: "0 0 38px", alignSelf: "stretch", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 700, color: "#8d94a8" }}>M1</Typography>
              </Box>
              <select
                value={musicUrl.startsWith("blob:") ? "__custom__" : musicUrl}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v !== "__custom__") setMusicUrl(v);
                }}
                style={{ background: "#0d0c12", color: "#c2c7d6", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "6px", fontFamily: "Outfit, sans-serif", fontSize: "11.5px", padding: "5px 8px", maxWidth: "220px" }}
              >
                <option value="">No music</option>
                {musicList.filter((t) => !t.vocal).length > 0 && (
                  <optgroup label="🎹 Instrumentals">
                    {musicList.filter((t) => !t.vocal).map((t) => (
                      <option key={t.file} value={`/music/${t.file}`} title={t.description || ""}>{t.title || t.file}</option>
                    ))}
                  </optgroup>
                )}
                {musicList.filter((t) => t.vocal).length > 0 && (
                  <optgroup label="🎤 With vocals">
                    {musicList.filter((t) => t.vocal).map((t) => (
                      <option key={t.file} value={`/music/${t.file}`} title={t.description || ""}>🎤 {t.title || t.file}</option>
                    ))}
                  </optgroup>
                )}
                {musicUrl.startsWith("blob:") && <option value="__custom__">Your file</option>}
              </select>
              <Box component="label" sx={{ cursor: "pointer", px: "9px", py: "4px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.14)", color: "#c2c7d6", fontFamily: font, fontSize: "10.5px", "&:hover": { borderColor: CHERRY, color: "#fff" } }}>
                + your track
                <input type="file" accept="audio/*" style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) setMusicUrl(URL.createObjectURL(f)); }} />
              </Box>
              {musicUrl && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>VOL</Typography>
                  <input type="range" min={0} max={100} value={Math.round(musicVol * 100)}
                    onChange={(e) => setMusicVol(parseInt(e.target.value, 10) / 100)}
                    style={{ width: "90px", accentColor: CHERRY, cursor: "pointer" }} />
                </Box>
              )}
              <Typography sx={{ fontFamily: font, fontSize: "10px", color: "#6e7488" }}>
                preview only — exports don't include music yet
              </Typography>
            </Box>
          </Box>

          {/* ── RIGHT: the cut player (docked; pops out to float anywhere) ── */}
          <Box sx={{ flex: { md: "0 0 300px" }, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
            <CutPlayer
              picks={picks}
              videoUrl={videoUrl}
              seekReq={seekReq}
              onProgress={setProg}
              floating={floating}
              onToggleFloat={() => setFloating((f) => !f)}
              music={{ url: musicUrl, vol: musicVol }}
            />
            {floating && (
              <Box sx={{ border: "1px dashed rgba(255,255,255,0.18)", borderRadius: "10px", p: "14px", textAlign: "center" }}>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#8d94a8" }}>
                  Player popped out — drag it anywhere, or dock it back here.
                </Typography>
              </Box>
            )}
            {!videoUrl && (
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: "#8d94a8", textAlign: "center" }}>
                Preview player unavailable for this demo source.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* ══════════════════════════ ET + TIMELINE, FUSED ══════════════════════════ */
const EtOverTimeline: React.FC = () => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());
  const [zoom, setZoom] = useState(0.55);
  const [fs, setFs] = useState(false);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [chatThread, setChatThread] = useState<ChatMsg[]>([]);
  const [chatBusy, setChatBusy] = useState(false);
  const chatTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "500px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  // demo-video picker announces a new /s/ src
  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) { setShareSrc(next); setLoaded(false); setPicks([]); setVideoUrl(""); setChatThread([]); setChatBusy(false); }
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  // ── the pick bridge: the embedded ET posts its ordered pick list on every change ──
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!e.data) return;
      // only trust our own iframe (works same-origin AND for the prod-hosted samples)
      if (frameRef.current && e.source !== frameRef.current.contentWindow) return;
      if (e.data.type === "cc-et-picks" && Array.isArray(e.data.picks)) {
        setPicks(e.data.picks as Pick[]);
        if (typeof e.data.video === "string" && e.data.video) setVideoUrl(e.data.video);
      } else if (e.data.type === "cc-et-ai-reply" && typeof e.data.text === "string") {
        if (chatTimerRef.current) { window.clearTimeout(chatTimerRef.current); chatTimerRef.current = null; }
        setChatBusy(false);
        setChatThread((t) => [...t, { role: "assistant", text: e.data.text, applied: Number(e.data.applied) || 0 }]);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const sendChat = (text: string) => {
    setChatThread((t) => [...t, { role: "user", text }]);
    setChatBusy(true);
    sendCmd({ cmd: "ai-chat", text });
    if (chatTimerRef.current) window.clearTimeout(chatTimerRef.current);
    chatTimerRef.current = window.setTimeout(() => {
      setChatBusy(false);
      setChatThread((t) => [...t, { role: "assistant", text: "⚠️ No reply from the editor. If AI isn't set up for this demo, open the editor's Black Cherry button to add a key — or the share link may have used up its guest AI." }]);
    }, 90000);
  };

  const src = inView ? shareSrc : "";

  // commands back INTO the embedded ET (trim / reorder / deselect) — explicit target origin
  const sendCmd: SendCmd = (c) => {
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    let target = window.location.origin;
    try { if (/^https?:\/\//.test(shareSrc)) target = new URL(shareSrc).origin; } catch (e) { /* ignore */ }
    w.postMessage({ type: "cc-et-cmd", ...c }, target);
  };

  return (
    <Box ref={hostRef} sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* editor window */}
      <Box sx={{ borderRadius: "14px 14px 0 0", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", borderBottom: "none", boxShadow: "0 40px 90px -55px rgba(0,0,0,0.9), 0 0 0 1px rgba(230,29,80,0.10)", background: "#0b0a0f" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "13px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <Box key={c} sx={{ width: 11, height: 11, borderRadius: "50%", background: c, opacity: 0.55 }} />
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: CHERRY, boxShadow: "0 0 0 3px rgba(230,29,80,0.18)" }} />
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12.5px", color: "#f3f4f9", display: { xs: "none", sm: "block" } }}>
              The spreadsheet — live
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: "2px", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", overflow: "hidden" }}>
            <Box component="button" onClick={() => setZoom((z) => clamp(z - 0.1, 0.35, 1.1))} title="Zoom out" sx={zoomBtn}>−</Box>
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#c2c7d6", px: "5px", minWidth: 34, textAlign: "center" }}>{Math.round(zoom * 100)}%</Typography>
            <Box component="button" onClick={() => setZoom((z) => clamp(z + 0.1, 0.35, 1.1))} title="Zoom in" sx={zoomBtn}>+</Box>
          </Box>
          <Box component="button" onClick={() => setFs(true)} title="Open full screen" sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "11px", px: "8px", py: "4px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>
            <OpenInFullIcon sx={{ fontSize: 13 }} /> Full screen
          </Box>
        </Box>

        <Box sx={{ position: "relative", height: { xs: "400px", md: "470px" } }}>
          {src ? (
            <iframe
              ref={frameRef}
              title="CherryClipz — live spreadsheet"
              src={src}
              allow="fullscreen; clipboard-write"
              onLoad={() => setLoaded(true)}
              style={iframeStyle(zoom)}
            />
          ) : null}
          {!loaded && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", background: "#0b0a0f" }}>
              <img src={logoSite} alt="" width={36} style={{ opacity: 0.85 }} />
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#9aa0b4" }}>Loading the live editor…</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* the seam — where spreadsheet meets timeline. Two ways in, side by side:
          tick clips yourself up top, or hand the same cut to Black Cherry right here. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", px: "13px", py: "9px", border: "1px solid rgba(230,29,80,0.35)", borderLeft: "1px solid rgba(255,255,255,0.12)", borderRight: "1px solid rgba(255,255,255,0.12)", background: "rgba(230,29,80,0.06)" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "11.5px", color: "#f4c9d6", textAlign: "center" }}>
          ▼ Tick clips above and they drop onto the track — or tell Black Cherry what you want:
        </Typography>
        <ChatDock thread={chatThread} busy={chatBusy} onSend={sendChat} />
      </Box>

      <EditTimeline picks={picks} videoUrl={videoUrl} sendCmd={sendCmd} />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", mt: "10px" }}>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8" }}>
          <Box component="span" sx={{ color: CHERRY }}>↑</Box> Real editor, real timeline — not a screenshot. Tick a checkbox and watch.
        </Typography>
        <DemoVideoPicker />
      </Box>

      {fs && (
        <Box onClick={() => setFs(false)} sx={{ position: "fixed", inset: 0, zIndex: 13000, background: "rgba(4,3,7,0.72)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: "8px", md: "18px" } }}>
          <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ width: "100%", maxWidth: "1500px", height: "92vh", display: "flex", flexDirection: "column", background: "#0b0a0f", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "14px", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: "12px", py: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
              <Box component="button" onClick={() => setFs(false)} title="Close" sx={{ cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "7px", color: "#c2c7d6", fontFamily: font, fontSize: "12px", px: "10px", py: "5px", "&:hover": { color: "#fff", borderColor: "rgba(255,255,255,0.3)" } }}>Close ×</Box>
            </Box>
            <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
              {src ? <iframe title="CherryClipz — full editor" src={src} allow="fullscreen; clipboard-write" style={iframeStyle(0.82)} /> : null}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* ══════════════════════════ THE DIMENSIONS ══════════════════════════ */
/** Every axis you can bend the spreadsheet along. Each one is a REAL control in the ET
 *  (ET_BEHAVIOR_SPEC.md §5–§9); the mini-viz is a zoomed-in echo of that control. */
type Dim = { icon: string; name: string; blurb: string; detail: string; viz: React.ReactNode };

const chip = (label: string, active = false) => (
  <Box key={label} sx={{ px: "7px", py: "3px", borderRadius: "999px", border: `1px solid ${active ? CHERRY : "rgba(255,255,255,0.18)"}`, background: active ? "rgba(230,29,80,0.16)" : "rgba(255,255,255,0.03)" }}>
    <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 600, color: active ? "#f4c9d6" : "#9aa0b4", lineHeight: 1 }}>{label}</Typography>
  </Box>
);

const vizRow = (children: React.ReactNode) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", alignItems: "center" }}>{children}</Box>
);

const DIMENSIONS: Dim[] = [
  {
    icon: "🔍", name: "Search",
    blurb: "Type a word — only the clips that say it remain.",
    detail: "Case-insensitive, typo-tolerant search over every transcript row. The context slider (0–5) re-includes N neighboring clips around each hit, dimmed, so you keep the flow of the conversation.",
    viz: vizRow([chip("“leap of faith”", true), chip("3 of 85 rows"), chip("context ±2")]),
  },
  {
    icon: "🗣", name: "Speaker",
    blurb: "Show only one voice — or everyone but them.",
    detail: "Every row knows who said it. Filter to any combination of speakers instantly — one interviewee, both hosts, everyone except the moderator.",
    viz: vizRow([chip("Speaker A", true), chip("Speaker B"), chip("Speaker C", true)]),
  },
  {
    icon: "😐", name: "Emotion",
    blurb: "Positive, neutral, negative — filter the mood.",
    detail: "Each sentence carries a sentiment. Keep only the upbeat moments, or only the heated ones — the argument montage builds itself.",
    viz: vizRow([chip("+ positive", true), chip("= neutral"), chip("− negative", true)]),
  },
  {
    icon: "⏱", name: "Time & length",
    blurb: "Only clips between 3 and 9 seconds. Done.",
    detail: "Range-filter by clip duration (to the decimal, if you want) and by text length in characters. Perfect for hunting short punchy lines or long monologues.",
    viz: vizRow([chip("3.0s — 9.0s", true), chip("≤ 120 chars")]),
  },
  {
    icon: "✂️", name: "Segment by",
    blurb: "Re-cut the whole sheet: sentences, words, N-second chunks, or custom merges.",
    detail: "The default row is a sentence — but one click re-slices the entire transcript into words, every-N-seconds intervals, or merged chunks by speaker/gap/character limit. Same footage, different grain.",
    viz: vizRow([chip("Sentences", true), chip("Words"), chip("Every 10s"), chip("Custom merge")]),
  },
  {
    icon: "🔥", name: "Virality score",
    blurb: "AI scores every clip 0–100 — sort the bangers to the top.",
    detail: "Black Cherry (with your own AI key) scores each clip's hook potential. Scores persist per video, unlock the Viral column, and become one more axis to sort and range-filter on.",
    viz: vizRow([chip("92", true), chip("87", true), chip("64"), chip("31"), chip("sort ↓")]),
  },
  {
    icon: "🫥", name: "Filler",
    blurb: "AI flags the throwaway lines — dim them, hide them, or show only them.",
    detail: "The ums, the “yeah so anyway”s — flagged by AI, then handled your way: dim to keep context, hide to clean the sheet, or “only” to review what's being cut.",
    viz: vizRow([chip("dim"), chip("hide", true), chip("only")]),
  },
  {
    icon: "␣", name: "Silences",
    blurb: "The spaces between speech are rows too — hide or reveal them.",
    detail: "Silence placeholders live between sentences. Hide them for a tight sheet, show them to find breathing room, pauses ≥1s get “…” marks.",
    viz: vizRow([chip("hide", true), chip("dim"), chip("only")]),
  },
  {
    icon: "🖊", name: "Pen highlight",
    blurb: "Drag across words — export just the highlighted span.",
    detail: "The pen tool highlights a phrase inside a clip; playback and export honor the marked span, not the whole row. “Marks only” plays just your highlights.",
    viz: (
      <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#c2c7d6" }}>
        …and <Box component="span" sx={{ background: "rgba(230,29,80,0.45)", borderRadius: "3px", px: "3px", color: "#fff" }}>this exact phrase</Box> is what exports
      </Typography>
    ),
  },
  {
    icon: "⊹", name: "Trim ±",
    blurb: "Nudge any in/out point by 0.1s — the neighbor gives way, nothing breaks.",
    detail: "The − / + steppers on every row ARE trimming: extend your out-point and the next clip's start slides forward (it shrinks, it doesn't get cut off). Words pulled in from a neighbor show in red. Non-destructive, always reversible.",
    viz: vizRow([chip("− 00:00:31 +", true), chip("neighbor auto-adjusts")]),
  },
  {
    icon: "🎞", name: "Pick order",
    blurb: "Tick clips in ANY order — that's your edit, like a playlist.",
    detail: "Selection is ordered: the 4th thing they said can open your cut. Drag rows to rearrange, pin picks to the top, and the export stitches them exactly in pick order.",
    viz: vizRow([chip("① hook", true), chip("② context", true), chip("③ payoff", true)]),
  },
  {
    icon: "🌐", name: "Translate",
    blurb: "Read the sheet in 47 languages.",
    detail: "Per-clip translations, cached per language, prefetched as you scroll — filter and pick in the language you think in.",
    viz: vizRow([chip("English", true), chip("Español"), chip("עברית"), chip("+44")]),
  },
];

const DimensionsGrid: React.FC = () => {
  const [active, setActive] = useState<number | null>(null);
  const a = active != null ? DIMENSIONS[active] : null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr", md: "repeat(4, 1fr)" }, gap: "10px" }}>
        {DIMENSIONS.map((d, i) => {
          const on = active === i;
          return (
            <Box
              key={d.name}
              component="button"
              onClick={() => setActive(on ? null : i)}
              sx={{
                textAlign: "left", cursor: "pointer", display: "flex", flexDirection: "column", gap: "7px",
                p: "13px", borderRadius: "12px",
                border: `1px solid ${on ? "rgba(230,29,80,0.55)" : "rgba(255,255,255,0.09)"}`,
                background: on ? "rgba(230,29,80,0.08)" : "rgba(255,255,255,0.025)",
                transition: "all .15s",
                "&:hover": { borderColor: "rgba(230,29,80,0.45)", transform: "translateY(-2px)" },
                "&:focus-visible": { outline: `2px solid ${CHERRY}`, outlineOffset: "2px" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box component="span" aria-hidden sx={{ fontSize: "16px", lineHeight: 1 }}>{d.icon}</Box>
                <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "14px", color: "#f3f4f9" }}>{d.name}</Typography>
              </Box>
              <Typography sx={{ fontFamily: font, fontSize: "12px", lineHeight: 1.45, color: "#9aa0b4" }}>{d.blurb}</Typography>
            </Box>
          );
        })}
      </Box>

      {/* zoomed-in detail strip for the active dimension */}
      {a && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "16px", alignItems: { md: "center" }, p: "16px", borderRadius: "12px", border: "1px solid rgba(230,29,80,0.4)", background: "linear-gradient(100deg, rgba(230,29,80,0.09), rgba(230,29,80,0.03))" }}>
          <Box sx={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: "8px", minWidth: { md: "260px" } }}>
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "16px", color: "#f3f4f9" }}>
              <Box component="span" aria-hidden sx={{ mr: "7px" }}>{a.icon}</Box>{a.name} — zoomed in
            </Typography>
            <Box sx={{ p: "10px", borderRadius: "9px", border: "1px solid rgba(255,255,255,0.12)", background: "#0b0a0f" }}>{a.viz}</Box>
          </Box>
          <Typography sx={{ fontFamily: font, fontSize: "13.5px", lineHeight: 1.6, color: "#c2c7d6" }}>{a.detail}</Typography>
        </Box>
      )}
    </Box>
  );
};

/* ══════════════════════════ THE COMBINATIONS COUNTER ══════════════════════════ */
/** Honest math: ordered picks of k clips out of n = n!/(n−k)! — computed in log space so the
 *  number is real, not hype. The point isn't the size; it's that filters collapse it to yours. */
const Combinations: React.FC = () => {
  const [clips, setClips] = useState(85);
  const [picked, setPicked] = useState(8);
  const k = Math.min(picked, clips);
  let log10 = 0;
  for (let i = 0; i < k; i += 1) log10 += Math.log10(clips - i);
  const exp = Math.floor(log10);
  const mant = (10 ** (log10 - exp)).toFixed(1);

  const slider = (label: string, value: number, set: (n: number) => void, min: number, max: number) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", flex: 1, minWidth: "200px" }}>
      <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 600, color: "#c2c7d6" }}>
        {label}: <Box component="span" sx={{ color: "#fff", fontFamily: mono }}>{value}</Box>
      </Typography>
      <input type="range" min={min} max={max} value={value}
        onChange={(e) => set(parseInt(e.target.value, 10))}
        style={{ width: "100%", accentColor: CHERRY, cursor: "pointer" }} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", p: { xs: "18px", md: "26px" }, borderRadius: "14px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}>
      <Box sx={{ display: "flex", gap: "18px", flexWrap: "wrap" }}>
        {slider("Clips in your video", clips, setClips, 10, 2500)}
        {slider("Clips you pick, in order", picked, setPicked, 2, 30)}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: mono, fontWeight: 700, fontSize: { xs: "30px", md: "44px" }, color: "#fff", lineHeight: 1 }}>
          {mant} × 10<Box component="sup" sx={{ fontSize: "0.55em", color: "#f4c9d6" }}>{exp}</Box>
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "14px", color: "#9aa0b4" }}>
          possible ordered cuts of {k} clips from {clips} — before a single filter, trim, or highlight.
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: font, fontSize: "13.5px", lineHeight: 1.6, color: "#c2c7d6", maxWidth: "760px" }}>
        That number is why prompting an AI for “the edit in your head” almost never returns it — your words can't
        pin one cut out of that many. The spreadsheet flips it: filters collapse the space to a handful of rows that
        matter, and <Box component="span" sx={{ color: "#f4c9d6", fontWeight: 700 }}>your hand picks the one cut that's yours</Box>.
        In control — never overwhelmed.
      </Typography>
    </Box>
  );
};

/* ══════════════════════════ TWO WAYS TO EDIT ══════════════════════════ */
const TwoWays: React.FC = () => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "14px" }}>
    {/* the machine path — deliberately cool/muted */}
    <Box sx={{ p: { xs: "18px", md: "24px" }, borderRadius: "14px", border: "1px solid rgba(140,158,178,0.25)", background: "linear-gradient(160deg, rgba(107,122,136,0.10), rgba(107,122,136,0.03))" }}>
      <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "18px", color: "#dfe6ee", mb: "8px" }}>
        🤖 Let Black Cherry edit
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "13.5px", lineHeight: 1.65, color: "#aab4c0" }}>
        Chat with the AI about your footage and it picks and orders clips for you — or scores every clip's virality,
        flags the filler, and applies 12 editorial lenses (Filmmaker's eye, Journalist's cut, Dramatist's arc…).
        It runs on <Box component="span" sx={{ fontWeight: 700 }}>your own AI key</Box>, right in your browser.
        Fast, tireless — and it makes <Box component="span" sx={{ fontStyle: "italic" }}>its</Box> version of your edit.
      </Typography>
    </Box>
    {/* the human path — cherry-warm */}
    <Box sx={{ p: { xs: "18px", md: "24px" }, borderRadius: "14px", border: "1px solid rgba(230,29,80,0.45)", background: "linear-gradient(160deg, rgba(230,29,80,0.12), rgba(230,29,80,0.03))" }}>
      <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "18px", color: "#fff", mb: "8px" }}>
        🍒 Or cherry-pick it yourself
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "13.5px", lineHeight: 1.65, color: "#e8cdd6" }}>
        We already asked the AI the expensive questions — transcribed every word, tagged every speaker and emotion,
        scored what it could. Now the decisions are yours: filter, tick, reorder, trim. Your hand on the mouse
        (or your thumb on the phone) makes <Box component="span" sx={{ fontWeight: 700 }}>the</Box> edit — the one
        in your head. Both paths live in the same spreadsheet; use either, or both on the same cut.
      </Typography>
    </Box>
  </Box>
);

/* ══════════════════════════ THE TIMELINE STORY / PREMIERE ══════════════════════════ */
const PremiereStory: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "22px" }}>
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
      <Typography sx={{ fontFamily: font, fontSize: "14px", lineHeight: 1.7, color: "#c2c7d6" }}>
        Editors trust the track. So the spreadsheet meets you there, today:
      </Typography>
      {[
        ["Available now", "Export your picks as EDL or Premiere XML — your cherry-picked cut opens as a sequence in Premiere, ready for fine trims.", true],
        ["Available now", "Or skip Premiere entirely: MP4, MP3, GIF, audiogram, SRT/VTT — stitched in your pick order.", true],
        ["On the roadmap", "The CherryClipz panel inside Premiere: the spreadsheet docked above your timeline — tick clips, they drop straight onto the sequence. Faster than the built-in transcript tool, because you're picking from a filtered, scored, searchable sheet.", false],
      ].map(([tag, text, now], i) => (
        <Box key={i} sx={{ display: "flex", gap: "11px", alignItems: "flex-start" }}>
          <Box sx={{ flexShrink: 0, mt: "2px", px: "7px", py: "3px", borderRadius: "999px", border: `1px solid ${now ? "rgba(40,200,120,0.5)" : "rgba(242,163,60,0.5)"}`, background: now ? "rgba(40,200,120,0.1)" : "rgba(242,163,60,0.1)" }}>
            <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 800, letterSpacing: "0.04em", color: now ? "#6de0a8" : "#f2c27c", whiteSpace: "nowrap" }}>{String(tag).toUpperCase()}</Typography>
          </Box>
          <Typography sx={{ fontFamily: font, fontSize: "13.5px", lineHeight: 1.6, color: "#c2c7d6" }}>{text}</Typography>
        </Box>
      ))}
    </Box>
    {/* mini diagram: ET panel above a PP-ish timeline */}
    <Box aria-hidden sx={{ flex: "0 0 auto", width: { xs: "100%", md: "380px" }, borderRadius: "12px", border: "1px solid rgba(255,255,255,0.1)", background: "#101017", p: "10px", display: "flex", flexDirection: "column", gap: "7px" }}>
      <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#6e7488" }}>Premiere Pro — with the CherryClipz panel</Typography>
      <Box sx={{ display: "flex", gap: "7px" }}>
        <Box sx={{ flex: 1.2, borderRadius: "7px", border: "1px solid rgba(230,29,80,0.5)", background: "rgba(230,29,80,0.06)", p: "7px", display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontFamily: font, fontSize: "9.5px", fontWeight: 700, color: "#f4c9d6" }}>🍒 CherryClipz sheet</Typography>
          {[1, 0, 1, 0, 0].map((on, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", border: `1px solid ${on ? CHERRY : "rgba(255,255,255,0.25)"}`, background: on ? CHERRY : "transparent" }} />
              <Box sx={{ flex: 1, height: 5, borderRadius: "2px", background: on ? "rgba(230,29,80,0.35)" : "rgba(255,255,255,0.09)" }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, borderRadius: "7px", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "74px" }}>
          <Typography sx={{ fontFamily: font, fontSize: "9.5px", color: "#8d94a8" }}>▶ program monitor</Typography>
        </Box>
      </Box>
      <Box sx={{ borderRadius: "7px", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", p: "6px", display: "flex", gap: "3px" }}>
        {[22, 12, 30, 18].map((w, i) => (
          <Box key={i} sx={{ flex: `0 0 ${w}%`, height: 18, borderRadius: "3px", background: i % 2 ? "rgba(79,141,249,0.4)" : "rgba(230,29,80,0.4)", border: "1px solid rgba(255,255,255,0.15)" }} />
        ))}
        <Typography sx={{ fontFamily: mono, fontSize: "8.5px", color: "#6e7488", alignSelf: "center", ml: "4px" }}>← your picks, on the sequence</Typography>
      </Box>
    </Box>
  </Box>
);

/* ══════════════════════════ SECTION SHELL ══════════════════════════ */
/** Sections are addressed like spreadsheet cells — because the page IS about a spreadsheet. */
const Section: React.FC<{ cell: string; title: React.ReactNode; sub?: string; children: React.ReactNode }> = ({ cell, title, sub, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", mt: { xs: "52px", md: "76px" } }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap" }}>
      <Box sx={{ px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.03)" }}>
        <Typography sx={{ fontFamily: mono, fontSize: "11px", fontWeight: 700, color: "#8d94a8", lineHeight: 1 }}>{cell}</Typography>
      </Box>
      <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: "24px", md: "32px" }, color: "#f3f4f9", letterSpacing: "-0.02em" }}>{title}</Typography>
    </Box>
    {sub && <Typography sx={{ fontFamily: font, fontSize: { xs: "14px", md: "15.5px" }, lineHeight: 1.6, color: "#9aa0b4", maxWidth: "820px", mt: "-6px" }}>{sub}</Typography>}
    {children}
  </Box>
);

/* ══════════════════════════ PAGE ══════════════════════════ */
const SpreadsheetTimelinePage: React.FC = () => (
  <Box sx={{ padding: { xs: "8px 16px 44px", md: "20px 48px 64px" } }}>
    <Box sx={{ width: "100%", maxWidth: "1480px", margin: "0 auto" }}>
      {/* eyebrow */}
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", mb: { xs: "18px", md: "24px" }, px: "12px", py: "6px", borderRadius: "999px", border: "1px solid rgba(230,29,80,0.35)", background: "rgba(230,29,80,0.08)" }}>
        <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: CHERRY, boxShadow: "0 0 0 3px rgba(230,29,80,0.18)" }} />
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12px", letterSpacing: "0.02em", color: "#f4c9d6" }}>
          Spreadsheet on top · timeline underneath — both live. No login to try.
        </Typography>
      </Box>

      {/* headline */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", mb: { xs: "22px", md: "30px" }, maxWidth: "980px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 800, color: "#f3f4f9", lineHeight: 1.04, letterSpacing: "-0.03em", fontSize: { xs: "34px", sm: "44px", md: "56px" } }}>
          Pick clips in the spreadsheet.<br />
          Watch them <Box component="span" sx={{ color: CHERRY }}>land on the timeline.</Box>
        </Typography>
        <Typography sx={{ fontFamily: font, fontWeight: 500, fontSize: { xs: "15px", md: "18px" }, lineHeight: 1.55, color: "#c2c7d6", maxWidth: "720px" }}>
          Your whole video, cut into rows — every sentence a clip, with speaker, emotion, and timing.
          Tick the ones you want and your edit assembles itself on the track below, in your order.
        </Typography>
      </Box>

      {/* THE fused surface */}
      <EtOverTimeline />

      {/* B2 — two ways to edit */}
      <Section
        cell="B2"
        title="Two ways to make the cut"
        sub="An AI can grasp your general idea — but the more complex the edit in your head, the less any prompt can pin it down. So we give you both hands: the machine's speed, and your final say."
      >
        <TwoWays />
      </Section>

      {/* C3 — the dimensions */}
      <Section
        cell="C3"
        title="Twelve dimensions of one video"
        sub="Every axis below is a real control in the editor above — click one to zoom in. Combine them freely: negative-sentiment clips under 6 seconds from Speaker B that mention “money”, sorted by virality. Instantly."
      >
        <DimensionsGrid />
      </Section>

      {/* D4 — the honest math */}
      <Section
        cell="D4"
        title="More cuts than you could dream — under your thumb"
        sub="Drag the sliders. The math is real (ordered selections: n!/(n−k)!). The point isn't the big number — it's that the spreadsheet turns it into a decision you can actually make."
      >
        <Combinations />
      </Section>

      {/* E5 — timeline / premiere */}
      <Section
        cell="E5"
        title="It all comes back to the timeline"
        sub="Rough-cut here in minutes; fine-trim wherever you're fastest — including Premiere."
      >
        <PremiereStory />
      </Section>

      {/* F6 — CTA */}
      <Box sx={{ mt: { xs: "52px", md: "76px" }, display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: { xs: "22px", md: "28px" }, color: "#f3f4f9", textAlign: "center", letterSpacing: "-0.02em" }}>
          Turn your video into a spreadsheet
        </Typography>
        <Box id="cc-demo-upload" sx={{ width: "100%", maxWidth: "760px" }}><UploadDownloadArea /></Box>
        <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8", textAlign: "center" }}>
          Upload a file or paste a link — pick your clips, export a reel. Longer videos take a little longer.
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default SpreadsheetTimelinePage;
